import classNames from 'classnames';
import { usePathname } from 'next/navigation';
import { Fragment } from 'react';
import { isInBuilder } from '~/app/lib/helpers';
import { useBuilder } from '~/app/lib/redux/selectors/builder-selector';
import { useDynamicGrid } from '~/app/lib/redux/selectors/dynamic-grid-selector';
import { useMediaQuery } from '~/app/lib/use-media-query';
import styles from './dynamic-grid.module.scss';
import { SegmentTrackingWrapper } from './segment/segment-tracking-container-wrapper';
import {
  ParentElementType,
  useDynamicGridContainer,
} from './use-dynamic-grid-container';
import { useStuck } from './use-stuck';
import { evaluateConditionalShowRules } from './utils';

type ContainerProps = {
  children: React.ReactNode;
  parentElement: ParentElementType;
};

const getHideValue = (hideValue: boolean, showAllMediaViewPorts: boolean) => {
  if (showAllMediaViewPorts) {
    return false;
  }
  return hideValue;
};

export const Container = ({ children, parentElement }: ContainerProps) => {
  const {
    id,
    alignItems,
    backgroundColor,
    backgroundImage,
    backgroundPosition,
    backgroundSize,
    backgroundRepeat,
    // borderRadius,
    borderTopLeftRadius,
    borderTopRightRadius,
    borderBottomRightRadius,
    borderBottomLeftRadius,
    borderColor,
    // borderWidth,
    borderTopWidth,
    borderRightWidth,
    borderBottomWidth,
    borderLeftWidth,
    borderStyle,
    boxShadow,
    display,
    elementType,
    gridTemplateColumns,
    height,
    isOverflowScroll,
    justifyContent,
    minWidthOverflowScroll,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    position,
    shouldShowGridOutlineStyle,
    top,
    width,
    zIndex,
    fullWidthStickyProps,
    isFullWidthSticky,
    tabletContainerOptions,
    mobileContainerOptions,
    hideOnTablet,
    hideOnMobile,
    hideOnDesktop,
    formattedConditionalShowRules,
    floatingEnabled,
    floatingStyle,
    trackingOptions,
  } = useDynamicGridContainer({ parentElement });
  const pathname = usePathname();
  const isBuilderPage = isInBuilder(pathname);
  const screenSize = useMediaQuery();
  const isMobile = screenSize === 'sm';
  const isTablet = screenSize === 'md';
  const { editor } = useBuilder();
  // Don't pass componentId - let useDynamicGrid use the DynamicGrid context instead
  const { marketplaceOffers } = useDynamicGrid();

  // Use the top value from the respective options based on screen size
  let topSpace = top;
  if (isTablet) topSpace = tabletContainerOptions.top;
  if (isMobile) topSpace = mobileContainerOptions.top;

  const [stickyContainerRef, isStuck, offsetLeft] = useStuck({
    topSpace: Number(topSpace?.replace('px', '')) || 96, // Default to 96px if not set and remove 'px' for calculations
    bottomSpace: 0,
  });

  let containerOptions = {
    alignItems,
    backgroundImage,
    backgroundPosition,
    backgroundSize,
    backgroundRepeat,
    borderColor,
    borderStyle,
    borderRadius: `${borderTopLeftRadius}px ${borderTopRightRadius}px ${borderBottomRightRadius}px ${borderBottomLeftRadius}px`,
    borderWidth: `${borderTopWidth}px ${borderRightWidth}px ${borderBottomWidth}px ${borderLeftWidth}px`,
    boxShadow,
    elementType,
    gridTemplateColumns,
    height,
    justifyContent,
    marginTop,
    marginRight,
    marginBottom,
    marginLeft,
    minWidthOverflowScroll,
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    position,
    top,
    width,
    zIndex,
    isFullWidthSticky,
    isOverflowScroll,
    backgroundColor,
  };
  if (isTablet) containerOptions = tabletContainerOptions;
  if (isMobile) containerOptions = mobileContainerOptions;

  const ContainerWrapper = trackingOptions?.enabled
    ? SegmentTrackingWrapper
    : Fragment;

  const segmentPropsWrapperProps = trackingOptions?.enabled
    ? {
        marketplaceOfferId: trackingOptions?.marketplaceOfferId,
        impressionSection: trackingOptions?.impressionSection,
        impressionPosition: trackingOptions?.impressionPosition,
        entityName: trackingOptions?.entityName,
        eventName: trackingOptions?.eventName || 'skuViewed',
        location: trackingOptions?.location,
        subLocation: trackingOptions?.subLocation,
      }
    : {};

  const selectedMarketplaceOffer = marketplaceOffers?.[0] || {};

  const getScreenDimensionsFromSize = (size: string) => {
    if (size === 'sm') return { width: 375, height: 667 }; // Mobile
    if (size === 'md') return { width: 768, height: 1024 }; // Tablet
    return { width: 1440, height: 900 }; // Desktop (lg)
  };
  const screenDimensions = getScreenDimensionsFromSize(screenSize);

  // Evaluate conditional show rules to determine if component should render
  const shouldShowComponent = formattedConditionalShowRules
    ? evaluateConditionalShowRules(
        formattedConditionalShowRules,
        marketplaceOffers,
        {
          pathname,
          screenSize,
          isMobile,
          isTablet,
          isBuilderPage,
          screenWidth: screenDimensions.width,
          screenHeight: screenDimensions.height,
          userAgent: '',
          ...selectedMarketplaceOffer,
        }
      )
    : true;

  if (!shouldShowComponent) {
    return null;
  }

  return (
    <ContainerWrapper
      {...segmentPropsWrapperProps}
      {...(trackingOptions?.enabled && {
        className: styles.segmentTrackingContainerWrapper,
      })}
    >
      <div
        id={isBuilderPage ? `${parentElement?.$unique_key}` : id}
        ref={
          containerOptions?.position === 'sticky'
            ? stickyContainerRef
            : undefined
        }
        key={parentElement?.$unique_key}
        className={classNames([
          styles.mediaQueryControl,
          {
            [styles.dottedBorder]: shouldShowGridOutlineStyle,
            [styles.hideOnTablet]: getHideValue(
              hideOnTablet,
              editor.showAllMediaViewPorts
            ),
            [styles.hideOnMobile]: getHideValue(
              hideOnMobile,
              editor.showAllMediaViewPorts
            ),
            [styles.hideOnDesktop]: getHideValue(
              hideOnDesktop,
              editor.showAllMediaViewPorts
            ),
          },
        ])}
        style={{
          alignItems: containerOptions?.alignItems,
          backgroundColor: containerOptions?.backgroundColor,
          backgroundImage: containerOptions?.backgroundImage,
          backgroundPosition: containerOptions?.backgroundPosition,
          backgroundRepeat: containerOptions?.backgroundRepeat,
          backgroundSize: containerOptions?.backgroundSize,
          borderRadius: containerOptions?.borderRadius,
          borderColor: containerOptions?.borderColor,
          borderWidth: containerOptions?.borderWidth,
          borderStyle: containerOptions?.borderStyle,
          boxShadow: containerOptions?.boxShadow,
          display,
          flexDirection: containerOptions?.elementType,
          gridTemplateColumns: containerOptions?.gridTemplateColumns,
          height: containerOptions?.height,
          overflow: containerOptions.isOverflowScroll,
          justifyContent: containerOptions?.justifyContent,
          marginTop: containerOptions?.marginTop,
          marginRight: containerOptions?.marginRight,
          marginBottom: containerOptions?.marginBottom,
          marginLeft:
            isStuck && containerOptions?.isFullWidthSticky
              ? `-${offsetLeft}px`
              : containerOptions?.marginLeft,
          minWidth: containerOptions?.minWidthOverflowScroll,
          paddingTop: containerOptions?.paddingTop,
          paddingRight: containerOptions?.paddingRight,
          paddingBottom: containerOptions?.paddingBottom,
          paddingLeft: containerOptions?.paddingLeft,
          position:
            containerOptions?.position as React.CSSProperties['position'],
          top: containerOptions?.top,
          width:
            isStuck && containerOptions?.isFullWidthSticky
              ? fullWidthStickyProps?.width
              : containerOptions?.width,
          zIndex: containerOptions?.zIndex,
          ...(floatingEnabled && {
            position: floatingStyle?.position,
            top: floatingStyle?.top,
            bottom: floatingStyle?.bottom,
            left: floatingStyle?.left,
            right: floatingStyle?.right,
            zIndex: floatingStyle?.zIndex,
          }),
        }}
      >
        {children}
      </div>
    </ContainerWrapper>
  );
};
